import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, CircularProgress,
  IconButton, ToggleButtonGroup, ToggleButton, useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';

const API = process.env.REACT_APP_API_URL || '';

const ESTADO_COLORS = {
  pendiente: 'warning',
  confirmado: 'success',
  cancelado: 'error',
  demorado: 'default',
  en_recepcion: 'info',
};

const FORM_VACIO = {
  medico_id: '',
  paciente_dni: '',
  paciente_id: null,
  paciente_nombre: '',
  paciente_email: '',
  paciente_telefono: '',
  paciente_obra_social: '',
  paciente_fecha_nacimiento: '',
  paciente_direccion: '',
  motivo: '',
  fecha: '',
  hora_inicio: '09:00',
  duracion: '30',
  estado: 'pendiente',
  notas: '',
  mensaje_paciente: '',
  nota_medico: '',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--c-primario)' } },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
};
const inputStyle = { style: { color: '#e0e0e0' } };
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };

const HOY = new Date().toISOString().split('T')[0];

function formatFechaLabel(f) {
  const d = new Date(f + 'T12:00:00');
  return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function sumarDias(fecha, dias) {
  const d = new Date(fecha + 'T12:00:00');
  d.setDate(d.getDate() + dias);
  return d.toISOString().split('T')[0];
}

function getLunesDeSemana(fecha) {
  const d = new Date(fecha + 'T12:00:00');
  const dia = d.getDay(); // 0=dom, 1=lun...
  const diff = dia === 0 ? -6 : 1 - dia;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function getDomingoDeSemana(lunes) {
  return sumarDias(lunes, 6);
}

function formatSemanaLabel(lunes) {
  const dom = getDomingoDeSemana(lunes);
  const dL = new Date(lunes + 'T12:00:00');
  const dD = new Date(dom + 'T12:00:00');
  return `${dL.getDate()}/${dL.getMonth()+1} — ${dD.getDate()}/${dD.getMonth()+1}`;
}

export default function Turnos() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rolActual = sessionStorage.getItem('rol') || '';
  const puedeHacerSobreturno = ['admin', 'recepcionista', 'medico'].includes(rolActual);

  // ── Filtros ──
  const [vistaFiltro, setVistaFiltro] = useState('dia'); // 'dia' | 'semana'
  const [fechaFiltro, setFechaFiltro] = useState(HOY);
  const [medicoFiltro, setMedicoFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [modoSearch, setModoSearch] = useState(false); // true = búsqueda libre activa

  const [obrasSociales, setObrasSociales] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [disponibilidades, setDisponibilidades] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [buscandoDni, setBuscandoDni] = useState(false);
  const [estadoDni, setEstadoDni] = useState(null);
  const [error, setError] = useState('');
  const [confirmarSobreturno, setConfirmarSobreturno] = useState(false);
  const [confirmarFueraDeAgenda, setConfirmarFueraDeAgenda] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);

  useEffect(() => {
    cargarMedicos();
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (!modoSearch) cargarTurnos(); }, [fechaFiltro, medicoFiltro, estadoFiltro, vistaFiltro]);

  async function cargarMedicos() {
    try {
      const medicosRes = await axios.get(`${API}/api/medicos`);
      const listaMedicos = medicosRes.data || [];
      setMedicos(listaMedicos);
      const dispPromises = listaMedicos.map(m =>
        axios.get(`${API}/api/medicos/${m.id}/disponibilidad`)
          .then(r => ({ id: m.id, horarios: r.data || [] }))
          .catch(() => ({ id: m.id, horarios: [] }))
      );
      const disps = await Promise.all(dispPromises);
      const dispMap = {};
      disps.forEach(d => { dispMap[d.id] = d.horarios; });
      setDisponibilidades(dispMap);
    } catch (err) { console.error(err); }
  }

  async function cargarTurnos() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vistaFiltro === 'semana') {
        const lunes = getLunesDeSemana(fechaFiltro);
        const domingo = getDomingoDeSemana(lunes);
        params.append('desde', lunes);
        params.append('hasta', domingo);
      } else {
        if (fechaFiltro) params.append('fecha', fechaFiltro);
      }
      if (medicoFiltro) params.append('medico_id', medicoFiltro);
      if (estadoFiltro) params.append('estado', estadoFiltro);
      const turnosRes = await axios.get(`${API}/api/turnos?${params.toString()}`);
      setTurnos(turnosRes.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function ejecutarBusqueda() {
    if (!busqueda.trim()) return;
    setModoSearch(true);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('q', busqueda.trim());
      if (medicoFiltro) params.append('medico_id', medicoFiltro);
      if (estadoFiltro) params.append('estado', estadoFiltro);
      const r = await axios.get(`${API}/api/turnos?${params.toString()}`);
      setTurnos(r.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function limpiarBusqueda() {
    setBusqueda('');
    setModoSearch(false);
    cargarTurnos();
  }

  async function cargarDatos() { await cargarTurnos(); }

  function toMin(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }

  function estaEnAgenda(medicoId, fecha, horaInicio) {
    const horarios = disponibilidades[medicoId] || [];
    if (!horarios.length) return false;
    const diasMap = { 0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miercoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sabado' };
    const diaNombre = diasMap[new Date(fecha + 'T12:00:00').getDay()];
    const bloquesDia = horarios.filter(h => h.dia === diaNombre);
    if (!bloquesDia.length) return false;
    const minHora = toMin(horaInicio);
    return bloquesDia.some(h => minHora >= toMin(h.desde) && minHora < toMin(h.hasta));
  }

  async function buscarPorDni(dni) {
    if (!dni || dni.length < 3) { setEstadoDni(null); return; }
    setBuscandoDni(true);
    try {
      const r = await axios.get(`${API}/api/pacientes/buscar?dni=${encodeURIComponent(dni)}`);
      const p = r.data;
      setForm(f => ({
        ...f,
        paciente_id: p.id,
        paciente_nombre: `${p.nombre} ${p.apellido}`.trim(),
        paciente_email: p.email || '',
        paciente_telefono: p.telefono || '',
        paciente_obra_social: p.obra_social || '',
        paciente_fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
        paciente_direccion: p.direccion || '',
      }));
      setEstadoDni('encontrado');
    } catch {
      setForm(f => ({ ...f, paciente_id: null }));
      setEstadoDni('no_encontrado');
    } finally {
      setBuscandoDni(false);
    }
  }

  function abrirNuevo() {
    setEditandoId(null);
    setEstadoDni(null);
    setForm(FORM_VACIO);
    setError('');
    setDialogOpen(true);
  }

  function abrirEditar(t) {
    setEditandoId(t.id);
    setEstadoDni(t.paciente_dni ? 'encontrado' : null);
    setForm({
      medico_id: t.medico_id || '',
      paciente_dni: t.paciente_dni || '',
      paciente_id: t.paciente_id || null,
      paciente_nombre: t.paciente_nombre || '',
      paciente_email: t.paciente_email || '',
      paciente_telefono: t.paciente_telefono || '',
      paciente_obra_social: t.paciente_obra_social || '',
      paciente_fecha_nacimiento: '',
      paciente_direccion: '',
      motivo: t.motivo || '',
      fecha: String(t.fecha).split('T')[0],
      hora_inicio: String(t.hora_inicio).substring(0, 5),
      duracion: String(t.duracion || 30),
      estado: t.estado || 'pendiente',
      notas: t.notas || '',
      mensaje_paciente: t.mensaje_paciente || '',
      nota_medico: t.nota_medico || '',
    });
    setError('');
    setDialogOpen(true);
  }

  function cerrar() {
    setDialogOpen(false);
    setEditandoId(null);
    setEstadoDni(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar() {
    if (!form.medico_id || !form.paciente_nombre || !form.fecha || !form.hora_inicio) {
      setError('Médico, paciente, fecha y hora son obligatorios.');
      return;
    }
    if (form.estado === 'en_recepcion') {
      if (!form.paciente_dni.trim()) { setError('El DNI es obligatorio para pasar a "En recepción".'); return; }
      if (!form.paciente_telefono.trim()) { setError('El teléfono es obligatorio para pasar a "En recepción".'); return; }
      if (!form.paciente_obra_social.trim()) { setError('La obra social es obligatoria para pasar a "En recepción".'); return; }
    }
    setError('');
    const payload = {
      medico_id: form.medico_id,
      paciente_id: form.paciente_id || null,
      paciente_dni: form.paciente_dni || '',
      paciente_nombre: form.paciente_nombre,
      paciente_email: form.paciente_email,
      paciente_telefono: form.paciente_telefono,
      motivo: form.motivo,
      fecha: form.fecha,
      hora_inicio: form.hora_inicio,
      duracion: parseInt(form.duracion) || 30,
      estado: form.estado || 'pendiente',
      notas: form.notas,
      sobreturno: 0,
      creado_por: editandoId ? undefined : (sessionStorage.getItem('nombre') || ''),
      mensaje_paciente: form.mensaje_paciente || null,
      nota_medico: form.nota_medico || null,
    };
    if (!editandoId && puedeHacerSobreturno && !estaEnAgenda(form.medico_id, form.fecha, form.hora_inicio)) {
      setPayloadPendiente(payload);
      setConfirmarFueraDeAgenda(true);
      return;
    }
    await enviarTurno(payload);
  }

  async function enviarTurno(payload) {
    setGuardando(true);
    try {
      if (editandoId) {
        await axios.put(`${API}/api/turnos/${editandoId}`, payload);
      } else {
        await axios.post(`${API}/api/turnos`, payload);
      }
      // Si es en_recepcion y hay paciente vinculado, actualizar sus datos
      if (payload.estado === 'en_recepcion' && form.paciente_id) {
        try {
          await axios.put(`${API}/api/pacientes/${form.paciente_id}`, {
            dni: form.paciente_dni,
            telefono: form.paciente_telefono,
            obra_social: form.paciente_obra_social,
            email: form.paciente_email,
            fecha_nacimiento: form.paciente_fecha_nacimiento || null,
            direccion: form.paciente_direccion,
          });
        } catch {}
      }
      cerrar();
      cargarDatos();
    } catch (err) {
      if (err.response?.status === 409 && puedeHacerSobreturno) {
        setPayloadPendiente(payload);
        setConfirmarSobreturno(true);
      } else {
        setError(err.response?.data?.error || 'Error al guardar.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarYGuardarSobreturno() {
    setConfirmarSobreturno(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    setGuardando(true);
    try {
      await axios.post(`${API}/api/turnos`, p);
      cerrar();
      cargarDatos();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el sobreturno.');
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarYGuardarFueraDeAgenda() {
    setConfirmarFueraDeAgenda(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    await enviarTurno(p);
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminár este turno?')) return;
    await axios.delete(`${API}/api/turnos/${id}`);
    cargarDatos();
  }

  function formatFecha(f) {
    return f ? String(f).split('T')[0].split('-').reverse().join('/') : '-';
  }

  return (
    <Box>
      {/* Dialog: horario ya ocupado (sobreturno) */}
      <Dialog open={confirmarSobreturno} onClose={() => setConfirmarSobreturno(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ff1744' }}>⚡ Horario ya ocupado</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Este horario ya tiene un turno asignado para otro paciente.
          </Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600 }}>
            ¿Confirmás el sobreturno? Se agregarán dos pacientes en el mismo horario.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarSobreturno(false)} sx={{ color: 'var(--c-acento)' }}>
            Cancelar
          </Button>
          <Button onClick={confirmarYGuardarSobreturno} variant="contained" disabled={guardando}
            sx={{ bgcolor: '#ff1744', '&:hover': { bgcolor: '#d50000' } }}>
            {guardando ? 'Guardando...' : 'Confirmar sobreturno'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: fuera de agenda del médico */}
      <Dialog open={confirmarFueraDeAgenda} onClose={() => setConfirmarFueraDeAgenda(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ffb74d' }}>📅 Fuera de agenda</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            El profesional no tiene disponibilidad marcada para ese día u horario.
          </Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600 }}>
            ¿Confirmás el turno de todas formas? Quedará registrado como turno excepcional.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarFueraDeAgenda(false)} sx={{ color: 'var(--c-acento)' }}>
            Cancelar
          </Button>
          <Button onClick={confirmarYGuardarFueraDeAgenda} variant="contained" disabled={guardando}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
            {guardando ? 'Guardando...' : 'Confirmar turno excepcional'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Gestión de Turnos
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
          Nuevo Turno
        </Button>
      </Box>

      {/* ── Barra de filtros ── */}
      <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2, mb: 2, border: '1px solid var(--c-primario)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>

        {/* Fila principal: todo en una línea */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

          {/* Búsqueda */}
          <TextField
            size="small"
            placeholder="Buscar nombre o DNI..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && ejecutarBusqueda()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--c-acento)', fontSize: 18 }} />
                </InputAdornment>
              ),
              style: { color: '#e0e0e0' },
            }}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
              '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
            }}
          />
          <Button
            variant="contained" size="small"
            onClick={ejecutarBusqueda}
            disabled={!busqueda.trim() || loading}
            sx={{ bgcolor: 'var(--c-acento)', color: '#000', fontWeight: 700, px: 2, height: 40,
              '&:hover': { bgcolor: 'var(--c-acento)', opacity: 0.85 },
              '&.Mui-disabled': { bgcolor: 'var(--c-primario)', color: '#555' },
            }}>
            Buscar
          </Button>
          {modoSearch && (
            <Button size="small" startIcon={<ClearIcon fontSize="small" />} onClick={limpiarBusqueda}
              sx={{ color: '#ffb74d', textTransform: 'none', fontSize: 12 }}>
              Limpiar
            </Button>
          )}

          {/* Separador vertical */}
          <Box sx={{ width: '1px', height: 32, bgcolor: 'var(--c-primario)', mx: 0.5, display: { xs: 'none', md: 'block' } }} />

          {/* Toggle Día / Semana */}
          <ToggleButtonGroup
            value={vistaFiltro} exclusive
            onChange={(_, v) => { if (v) { setVistaFiltro(v); if (modoSearch) limpiarBusqueda(); } }}
            size="small"
            sx={{
              opacity: modoSearch ? 0.45 : 1,
              '& .MuiToggleButton-root': {
                color: '#90a4ae', borderColor: 'var(--c-primario)', fontSize: 12, px: 1.5, py: 0.5,
                textTransform: 'none',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                bgcolor: 'var(--c-acento)', color: '#000', fontWeight: 700,
                '&:hover': { bgcolor: 'var(--c-acento)' },
              },
            }}>
            <ToggleButton value="dia">Día</ToggleButton>
            <ToggleButton value="semana">Semana</ToggleButton>
          </ToggleButtonGroup>

          {/* Navegación de fecha */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: modoSearch ? 0.45 : 1, pointerEvents: modoSearch ? 'none' : 'auto' }}>
            <IconButton size="small" onClick={() => setFechaFiltro(f => sumarDias(f, vistaFiltro === 'semana' ? -7 : -1))} sx={{ color: 'var(--c-acento)' }}>
              <ChevronLeftIcon />
            </IconButton>
            <TextField
              type="date" size="small" value={fechaFiltro}
              onChange={e => setFechaFiltro(e.target.value)}
              inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark', fontSize: 13 } }}
              sx={{ width: 148, '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' } }}
            />
            <IconButton size="small" onClick={() => setFechaFiltro(f => sumarDias(f, vistaFiltro === 'semana' ? 7 : 1))} sx={{ color: 'var(--c-acento)' }}>
              <ChevronRightIcon />
            </IconButton>
            {fechaFiltro !== HOY && (
              <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
                onClick={() => setFechaFiltro(HOY)}
                sx={{ color: '#ffb74d', textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}>
                Hoy
              </Button>
            )}
            <Typography sx={{ fontSize: 12, color: '#90a4ae', display: { xs: 'none', lg: 'block' } }}>
              {vistaFiltro === 'semana'
                ? formatSemanaLabel(getLunesDeSemana(fechaFiltro))
                : formatFechaLabel(fechaFiltro)}
            </Typography>
          </Box>

          {/* Médico */}
          {rolActual !== 'medico' && (
            <FormControl size="small" sx={{ minWidth: 170, opacity: modoSearch ? 0.45 : 1 }}>
              <InputLabel sx={{ color: 'var(--c-acento)', fontSize: 13 }}>Médico</InputLabel>
              <Select value={medicoFiltro} label="Médico" onChange={e => setMedicoFiltro(e.target.value)}
                sx={{ color: '#e0e0e0', fontSize: 13, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
                MenuProps={{ PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } }}>
                <MenuItem value=""><em style={{ color: '#90a4ae' }}>Todos</em></MenuItem>
                {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Contador */}
          <Typography sx={{ fontSize: 12, color: '#90a4ae', ml: 'auto' }}>
            {!loading && `${turnos.length} turno${turnos.length !== 1 ? 's' : ''}${modoSearch ? ` · "${busqueda}"` : ''}`}
          </Typography>
        </Box>

        {/* Chips de estado */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {['', 'pendiente', 'confirmado', 'en_recepcion', 'demorado', 'atendido', 'cancelado'].map(v => (
            <Chip key={v} size="small"
              label={v === '' ? 'Todos' : v === 'en_recepcion' ? 'En recepción' : v.charAt(0).toUpperCase() + v.slice(1)}
              onClick={() => setEstadoFiltro(v)}
              sx={{
                cursor: 'pointer', fontSize: 11,
                bgcolor: estadoFiltro === v ? 'var(--c-acento)' : 'var(--c-primario)',
                color: estadoFiltro === v ? '#000' : '#e0e0e0',
                fontWeight: estadoFiltro === v ? 700 : 400,
              }} />
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ bgcolor: 'var(--c-superficie)', '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' } }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
                <TableCell>DNI</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Médico</TableCell>
                {(vistaFiltro === 'semana' || modoSearch) && <TableCell>Fecha</TableCell>}
                <TableCell>Hora</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Registrado por</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {turnos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={modoSearch || vistaFiltro === 'semana' ? 8 : 7} align="center" sx={{ color: '#b0b0b0' }}>
                    {modoSearch ? `Sin resultados para "${busqueda}"` : 'No hay turnos para esta fecha'}
                  </TableCell>
                </TableRow>
              ) : turnos.map(t => (
                <TableRow key={t.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{t.paciente_dni || '—'}</TableCell>
                  <TableCell>{t.paciente_nombre || '—'}</TableCell>
                  <TableCell>{t.medico_nombre || '—'}</TableCell>
                  {(vistaFiltro === 'semana' || modoSearch) && <TableCell>{formatFecha(t.fecha)}</TableCell>}
                  <TableCell>{String(t.hora_inicio).substring(0, 5)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={t.estado || 'pendiente'} color={ESTADO_COLORS[t.estado] || 'default'} size="small" />
                      {!!t.sobreturno && <Chip label="⚡ SOBRETURNO" size="small" sx={{ bgcolor: '#ff1744', color: '#fff', fontWeight: 700, fontSize: 10 }} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {t.creado_por
                      ? <><Typography sx={{ fontSize: 12 }}>{t.creado_por}</Typography>
                          <Typography sx={{ fontSize: 11, color: 'var(--c-acento)' }}>{formatFecha(t.created_at)}</Typography></>
                      : <Typography sx={{ color: '#666', fontSize: 12 }}>—</Typography>}
                  </TableCell>
                  <TableCell>
                    {rolActual === 'medico' && (
                      <Button size="small"
                        startIcon={<MedicalServicesIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/consulta/${t.id}`)}
                        sx={{ color: '#81c784', minWidth: 0, px: 1, fontWeight: 700 }}
                        title="Atender paciente">
                        Atender
                      </Button>
                    )}
                    <Button size="small" sx={{ color: 'var(--c-acento)' }} onClick={() => abrirEditar(t)}>Gestionar</Button>
                    {['admin', 'recepcionista'].includes(rolActual) && (
                      <Button size="small" disabled title="Próximamente — esperando número de WhatsApp"
                        sx={{ color: '#25D366', opacity: 0.45, minWidth: 0, fontSize: 11 }}>
                        ✉ Confirmar
                      </Button>
                    )}
                    {['admin', 'recepcionista'].includes(rolActual) && (
                      <Button size="small" color="error" onClick={() => eliminar(t.id)}>Eliminar</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={cerrar} maxWidth="md" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle>{editandoId ? 'Gestionar Turno' : 'Nuevo Turno'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>

          {/* ── Dos columnas ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Columna izquierda — Paciente */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Paciente
              </Typography>
              <Box>
                <TextField fullWidth label="DNI *" value={form.paciente_dni}
                  onChange={e => { setForm({ ...form, paciente_dni: e.target.value, paciente_id: null }); setEstadoDni(null); }}
                  onBlur={e => buscarPorDni(e.target.value)}
                  InputLabelProps={labelStyle} inputProps={{ ...inputStyle, maxLength: 20 }} sx={fieldSx} />
                {buscandoDni && <Typography sx={{ fontSize: 11, color: 'var(--c-acento)', mt: 0.5 }}>Buscando...</Typography>}
                {estadoDni === 'encontrado' && <Typography sx={{ fontSize: 11, color: '#66bb6a', mt: 0.5 }}>✓ Datos cargados automáticamente</Typography>}
                {estadoDni === 'no_encontrado' && <Typography sx={{ fontSize: 11, color: '#ffb74d', mt: 0.5 }}>Paciente no registrado — completá los datos</Typography>}
              </Box>
              <TextField label="Nombre *" value={form.paciente_nombre}
                onChange={e => setForm({ ...form, paciente_nombre: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Email" value={form.paciente_email}
                onChange={e => setForm({ ...form, paciente_email: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Teléfono" value={form.paciente_telefono}
                onChange={e => setForm({ ...form, paciente_telefono: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Motivo de consulta" value={form.motivo}
                onChange={e => setForm({ ...form, motivo: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>

            {/* Columna derecha — Turno */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Turno
              </Typography>
              <FormControl>
                <InputLabel sx={{ color: 'var(--c-acento)' }}>Médico *</InputLabel>
                <Select value={form.medico_id} label="Médico *"
                  onChange={e => setForm({ ...form, medico_id: e.target.value })}
                  sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                  {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre} — {m.especialidad}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Fecha *" type="date" value={form.fecha} fullWidth
                onChange={e => setForm({ ...form, fecha: e.target.value })}
                InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Hora *" type="time" value={form.hora_inicio}
                  onChange={e => setForm({ ...form, hora_inicio: e.target.value })}
                  InputLabelProps={{ ...labelStyle, shrink: true }} inputProps={inputStyle} sx={fieldSx} />
                <TextField label="Duración (min)" type="number" value={form.duracion}
                  onChange={e => setForm({ ...form, duracion: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={{ ...inputStyle, min: 5, step: 5 }} sx={fieldSx} />
              </Box>
              <FormControl>
                <InputLabel sx={{ color: 'var(--c-acento)' }}>Estado</InputLabel>
                <Select value={form.estado} label="Estado"
                  onChange={e => setForm({ ...form, estado: e.target.value })}
                  sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="confirmado">Confirmado</MenuItem>
                  <MenuItem value="en_recepcion">En recepción</MenuItem>
                  <MenuItem value="demorado">Demorado</MenuItem>
                  <MenuItem value="atendido">Atendido</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Notas internas" value={form.notas} multiline rows={3}
                onChange={e => setForm({ ...form, notas: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>
          </Box>

          {/* ── Datos requeridos para recepción ── */}
          {form.estado === 'en_recepcion' && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '2px solid #42a5f5', bgcolor: 'rgba(66,165,245,0.07)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#42a5f5', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                📋 Datos requeridos para recepción
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="DNI *" value={form.paciente_dni}
                  onChange={e => setForm({ ...form, paciente_dni: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
                  error={!form.paciente_dni.trim()}
                  helperText={!form.paciente_dni.trim() ? 'Requerido' : ''}
                  FormHelperTextProps={{ style: { color: '#ef5350' } }} />
                <TextField label="Teléfono *" value={form.paciente_telefono}
                  onChange={e => setForm({ ...form, paciente_telefono: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
                  error={!form.paciente_telefono.trim()}
                  helperText={!form.paciente_telefono.trim() ? 'Requerido' : ''}
                  FormHelperTextProps={{ style: { color: '#ef5350' } }} />
                <FormControl fullWidth sx={fieldSx}
                  error={!form.paciente_obra_social.trim()}>
                  <InputLabel sx={{ color: 'var(--c-acento)' }}>Obra social *</InputLabel>
                  <Select value={form.paciente_obra_social} label="Obra social *"
                    onChange={e => setForm({ ...form, paciente_obra_social: e.target.value })}
                    sx={{ color: '#e0e0e0' }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } }}>
                    {obrasSociales.map(os => <MenuItem key={os.id} value={os.nombre}>{os.nombre}</MenuItem>)}
                  </Select>
                  {!form.paciente_obra_social.trim() && <Typography sx={{ fontSize: 11, color: '#ef5350', mt: 0.5, ml: 1.5 }}>Requerido</Typography>}
                </FormControl>
                <TextField label="Email" value={form.paciente_email}
                  onChange={e => setForm({ ...form, paciente_email: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                <TextField label="Fecha de nacimiento (opcional)" type="date" fullWidth value={form.paciente_fecha_nacimiento}
                  onChange={e => setForm({ ...form, paciente_fecha_nacimiento: e.target.value })}
                  InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                  inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
                <TextField label="Dirección (opcional)" value={form.paciente_direccion}
                  onChange={e => setForm({ ...form, paciente_direccion: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              </Box>
            </Box>
          )}

          {/* ── Ancho completo — Mensajes ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {form.mensaje_paciente ? (
              <Box sx={{ p: 1.5, bgcolor: 'rgba(144,202,249,0.08)', borderRadius: 2, borderLeft: '3px solid var(--c-acento)' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>
                  💬 Mensaje del paciente:
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#e0e0e0' }}>{form.mensaje_paciente}</Typography>
              </Box>
            ) : null}
            <TextField label="📋 Nota para el médico" value={form.nota_medico} multiline rows={2}
              onChange={e => setForm({ ...form, nota_medico: e.target.value })}
              placeholder="Indicaciones para el médico antes de la consulta..."
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={{
                ...fieldSx, '& .MuiOutlinedInput-root fieldset': { borderColor: '#66bb6a' },
              }} />
          </Box>

          {error && <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrar} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
